import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown } from "lucide-react";
import { useTrial } from "@/hooks/use-trial";
import { isNativeIosApp } from "@/lib/platform";
import { openBuukdSupportWhatsApp } from "@/lib/support";
import { useBusiness } from "@/hooks/use-business";
import { isPerBookingBilling } from "@/lib/billing-model";

export function TrialUpgradeDialog() {
  const navigate = useNavigate();
  const { isInGrace } = useTrial();
  const { business } = useBusiness();
  const isNativeIos = isNativeIosApp();
  const perBookingActivation = isPerBookingBilling(business);

  const handleUpgrade = () => {
    if (isNativeIos) {
      openBuukdSupportWhatsApp();
      return;
    }

    navigate("/app/billing");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Blurred background overlay */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-md" />
      
      {/* Dialog */}
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-card border-2 border-primary/20 shadow-2xl">
          <DialogHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Crown className="h-8 w-8 text-primary" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground">
              {isNativeIos
                ? "Contact support to continue"
                : perBookingActivation
                  ? "Pay activation fee to continue"
                  : "Upgrade to Access the Full Potential of Buukd"}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground pt-2">
              {isNativeIos ? (
                <>
                  Your account access needs setup. Please contact Buukd support for help restoring access.
                </>
              ) : perBookingActivation ? (
                <>
                  Your free setup period has ended. Pay the one-time activation fee on Billing to unlock your account.
                  After that, there is no monthly subscription — you only pay as customers book online.
                </>
              ) : (
                <>
                  Your {isInGrace ? "free trial has ended, and your grace period is ending soon" : "free trial has expired"}.
                  Unlock limitless booking possibilities and access valuable features that fuel your business growth.
                  Get more capacity and unleash your full potential with our paid plans.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-4">
            <Button
              onClick={handleUpgrade}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              {isNativeIos ? "Contact support" : perBookingActivation ? "Go to Billing" : "Upgrade Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
